import React from "react";
import { Link } from "react-router-dom";
import MarketRegForm from "@/components/site/MarketRegForm";
import TestimonialsSections from "@/components/site/TestimonialsSections";
import { CheckCircle2, Sparkles, Flame, Target, Heart, Moon, Video, Clock, Calendar, User } from "lucide-react";

function InlineForm({ programType, heading, submitLabel, optionHeading, optionType, optionNote }) {
  return <MarketRegForm programType={programType} tid="ins" submitTestId="ins-secure" heading={heading} submitLabel={submitLabel} optionHeading={optionHeading} optionType={optionType} optionNote={optionNote} />;
}

function PageShell({ programType, eyebrow, headline, headlineEm, intro1, intro2, leftBody, testid, formProgramType, formHeading, formSubmitLabel, formOptionHeading, formOptionType, formOptionNote, belowContent }) {
  return (
    <div className="fade-up" data-testid={testid}>
      <section className="container-ic pt-14 pb-10 sm:pt-20 sm:pb-12">
        <p className="text-xs uppercase tracking-[0.25em] text-terracotta">{eyebrow}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2422] leading-[1.08] mt-4">{headline} {headlineEm && <span className="italic text-terracotta">{headlineEm}</span>}</h1>
        {intro1 && <p className="mt-7 text-xl sm:text-2xl text-[#3D3633] leading-relaxed max-w-2xl font-light">{intro1}</p>}
        {intro2 && <p className="mt-4 text-base sm:text-lg text-[#7E736E] leading-relaxed max-w-2xl font-light">{intro2}</p>}
      </section>
      <section className="container-ic pb-16 sm:pb-20 grid lg:grid-cols-2 gap-10">
        <div>{leftBody}</div>
        <div><InlineForm programType={formProgramType || programType} heading={formHeading} submitLabel={formSubmitLabel} optionHeading={formOptionHeading} optionType={formOptionType} optionNote={formOptionNote}/></div>
      </section>
      {belowContent && <section className="container-ic pb-16 sm:pb-20">{belowContent}</section>}
    </div>
  );
}

const SHIFT_ICONS = [Sparkles, Heart, Target, User, CheckCircle2];

export const MeditationEnlightenmentPage = () => (
  <PageShell testid="page-meditation_enlightenment" programType="meditation_enlightenment"
    formOptionHeading="Choose Your Session" formOptionType="session"
    eyebrow="Meditation Enlightenment" headline="You've already felt a calm mind." headlineEm="Now return to it, consciously."
    intro1="Return to your natural state of calm, and discover meditation as something already within you."
    intro2="Live 40-minute iChaitanya Dhyan (Meditation) Enlightenment with Umesh Misra"
    belowContent={
      <div data-testid="med-testimonial-preview">
        <h2 className="font-serif text-3xl sm:text-4xl">Experiences from iChaitanya Participants</h2>
        <div className="mt-8"><TestimonialsSections preview limit={4} showStatic={false} /></div>
        <div className="mt-8"><Link to="/testimonials" data-testid="med-view-all-testimonials" className="btn-primary">View All Testimonials</Link></div>
      </div>
    }
    leftBody={<>
      <h2 className="font-serif text-3xl">A significant shift begins within you.</h2>
      <ol className="mt-6 space-y-3">
        {["A confidence that you can meditate","An awareness that you can remain 'thought-free' for minutes that pass like seconds","How to create an 'instant mental shift' in real life situations","How to nurture your mind like a muscle","A calm mind, not by control but by understanding"].map((t,i)=>{const I=SHIFT_ICONS[i]; return (
          <li key={i} className="soft-card !p-4 flex gap-3 items-start"><I className="text-terracotta shrink-0 mt-1" size={20}/><span className="text-[#3D3633]">{t}</span></li>
        );})}
      </ol>
      <div className="soft-card bg-[#F3EFEA] mt-6">
        <h3 className="font-serif text-2xl">All that is needed:</h3>
        <ul className="mt-4 space-y-3 text-[#3D3633]">
          <li className="flex gap-3"><CheckCircle2 className="text-terracotta shrink-0 mt-1" size={18}/><span>Sit in a very well lit room/place</span></li>
          <li className="flex gap-3"><CheckCircle2 className="text-terracotta shrink-0 mt-1" size={18}/><span>Willingness to show up</span></li>
          <li className="flex gap-3"><CheckCircle2 className="text-terracotta shrink-0 mt-1" size={18}/><span>Openness to whatever you experience.</span></li>
        </ul>
      </div>
    </>}/>
);

export const BlissPathPage = () => (
  <PageShell testid="page-bliss_path" programType="bliss_path"
    formHeading="Join The Bliss Path Program" formOptionHeading="Choose Your Batch" formOptionType="range"
    eyebrow="Bliss Path Program" headline="You've already felt a calm mind." headlineEm="Now return to it, consciously."
    intro1="Return to your natural state of calm through Bliss Path Program. Choose the batch that works for your week."
    intro2="A guided meditation journey for inner calm, emotional balance, positivity, and conscious living."
    leftBody={<>
      <div className="soft-card">
        <h2 className="font-serif text-2xl">Designed for anyone seeking freedom from:</h2>
        <ul className="mt-5 space-y-3 text-[#3D3633]">
          {["constant overthinking","anxiety","negative thoughts","mental restlessness","emotional heaviness"].map((t,i)=>(
            <li key={i} className="flex gap-3 items-center"><Sparkles className="text-terracotta" size={18}/>{t}</li>
          ))}
        </ul>
      </div>
      <div className="soft-card bg-[#F3EFEA] mt-6">
        <h2 className="font-serif text-2xl">What You'll Practice:</h2>
        <ul className="mt-5 space-y-4">
          <li className="flex gap-3 items-start"><Heart className="text-terracotta shrink-0 mt-1" size={20}/><div><div className="font-serif text-lg">Anand Dhyan</div><div className="text-sm text-[#7E736E]">(Bliss Meditation)</div></div></li>
          <li className="flex gap-3 items-start"><Flame className="text-terracotta shrink-0 mt-1" size={20}/><div><div className="font-serif text-lg">Agni Dhyan</div><div className="text-sm text-[#7E736E]">(Release Meditation)</div></div></li>
          <li className="flex gap-3 items-start"><Target className="text-terracotta shrink-0 mt-1" size={20}/><div><div className="font-serif text-lg">Aekam Dhyan</div><div className="text-sm text-[#7E736E]">(Intention Meditation)</div></div></li>
        </ul>
      </div>
    </>}/>
);

export const AuraNightPage = () => (
  <PageShell testid="page-aura_night" programType="aura_night"
    formHeading="Begin Aura Night Meditation" formOptionHeading="Choose Your Journey" formOptionType="range"
    eyebrow="Aura Night Page · 40 Nights of Positivity" headline="Aura – Night Meditation" headlineEm=""
    intro1="The mind absorbs a lot through the day. Aura – Night Meditation is a gentle 10-minute meditation experience designed to help you release the day, quiet the mind, and reconnect with peace, positivity, and renewed energy before tomorrow begins."
    leftBody={<div className="rounded-[2rem] bg-[#4A3B32] text-[#EBE5D9] p-10">
      <ul className="space-y-5">
        <li className="flex gap-3 items-center"><Calendar className="text-terracotta shrink-0" size={22}/><span className="font-serif text-2xl text-white">Journey — 25/29 Days</span></li>
        <li className="flex gap-3 items-center"><Clock className="text-terracotta shrink-0" size={22}/><span className="font-serif text-2xl text-white">Daily Time — 9:00 PM to 9:10 PM</span></li>
        <li className="flex gap-3 items-center"><Video className="text-terracotta shrink-0" size={22}/><span className="font-serif text-2xl text-white">Live on Zoom</span></li>
      </ul>
    </div>}/>
);

export const OneToOnePage = () => (
  <PageShell testid="page-one_to_one" programType="one_to_one"
    formProgramType="clarity_call" formHeading="Book Your Clarity Call" formSubmitLabel="Book My Clarity Call"
    formOptionHeading="Choose Your Session:"
    formOptionNote="Our team will call you to fix the exact timing of your 40-minute session between 7:00 AM and 9:00 PM on your selected date."
    eyebrow="iC One-to-One Page" headline="A private space for your inner journey" headlineEm=""
    intro1="A deeply personal one-to-one meditative experience for inner clarity, emotional balance, and stillness."
    leftBody={<>
      <div className="soft-card">
        <h2 className="font-serif text-2xl">Designed for people seeking:</h2>
        <ul className="mt-5 space-y-3 text-[#3D3633]">
          {[[Sparkles,"deeper attention"],[Heart,"emotional clarity"],[Moon,"calmness"],[User,"personal guidance"],[Target,"inner stillness"]].map(([I,t],i)=>(
            <li key={i} className="flex gap-3 items-center"><I className="text-terracotta" size={18}/>{t}</li>
          ))}
        </ul>
      </div>
      <div className="soft-card bg-[#F3EFEA] mt-6">
        <ul className="space-y-3 text-[#3D3633]">
          {[[Video,"Private Guided Sessions"],[Video,"Live on Zoom"],[Calendar,"Flexible Scheduling"],[Sparkles,"Personalised Experience"]].map(([I,t],i)=>(
            <li key={i} className="flex gap-3 items-center"><I className="text-terracotta" size={18}/>{t}</li>
          ))}
        </ul>
      </div>
    </>}/>
);
